import {AfterViewChecked, AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {PlyrPlayerComponent} from '../plyr-player/plyr-player.component';
import Plyr from 'plyr';
import {fadeInAnimation} from 'src/app/animations';
import {PrismicEmbed} from 'src/app/backend/elearning-api/trainings/get-trainings.response';
import {PrivacyService} from 'src/app/shared/privacy.service';
import {DeviceDetectorService} from 'ngx-device-detector';

@Component({
  selector: 'app-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  animations: [fadeInAnimation],
})
export class VideoComponent implements AfterViewInit, OnChanges, AfterViewChecked {
  @Input() video?: PrismicEmbed;
  @Input() autoplay?: boolean;
  @Input() timestamp?: number;
  videoPlaying = false;
  videoStarted = false;
  // used to re-set the video-URL
  private viewMustUpdate = true;
  readonly cookiesEnabled$ = this._privacyService.areCookiesEnabled();

  // get the component instance to have access to plyr instance
  @ViewChild(PlyrPlayerComponent)
  plyr?: PlyrPlayerComponent;

  options = {
    controls: [
      'play-large', // The large play button in the center
      'play', // Play/pause playback
      'progress', // The progress bar and scrubber for playback and buffering
      'current-time', // The current time of playback
      'duration', // The full duration of the media
      'volume', // Volume control
      'fullscreen', // Toggle fullscreen
      'settings', // Settings for quality, speed, ...
    ],
    settings: [
      'speed', // only show the setting for playback-speed
    ],
    speed: {
      selected: 1,
      options: [0.75, 1, 1.25, 1.5, 1.7, 2],
    },
    // plyr uses an old API-URL which throws errors for private videos!
    // redirect call to our backend
    urls: {
      vimeo: {
        api: '/vimeo_api/videos/{0}.json',
      },
    },
    // vimeo supports passing a 'do not track'-flag
    vimeo: {
      dnt: true,
      playsinline: true,
    },
    fullscreen: {
      iosNative: true,
    },
    // currently, only speed is directly shown in the UI, so this is sufficient
    // in future, maybe translate the other parts too for the sake of a11y
    i18n: {
      speed: this._translateService.instant('VIDEO.SPEED'),
    },
  };

  played(event: Plyr.PlyrEvent) {
    this.videoPlaying = true;
  }

  loaded(event: Plyr.PlyrEvent) {
    // the blur should only be visible when the video wasn't started yet
    if (!this.videoStarted) {
      this.videoStarted = true;
    }
  }

  paused(event: Plyr.PlyrEvent) {
    this.videoPlaying = false;
  }

  constructor(
    private readonly _privacyService: PrivacyService,
    private readonly _translateService: TranslateService,
    private readonly _deviceDetector: DeviceDetectorService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.currentValue || changes.video) {
      this.viewMustUpdate = true;
      this.videoStarted = false;
    }
  }

  ngAfterViewInit() {
    this.setVimeoSource();
  }

  ngAfterViewChecked() {
    // when user switches between two video-slides, we have to change the video-source!
    if (this.viewMustUpdate) {
      this.setVimeoSource();
    }
  }

  private setVimeoSource() {
    if (this.plyr && this.video && this.video.embed_url) {
      this.plyr.player.source = {
        type: 'video',
        sources: [
          {
            src: this.video.embed_url,
            provider: 'vimeo',
          },
        ],
      };
      this.plyr.player.poster = '';
      // view is updated
      this.viewMustUpdate = false;

      this.plyr.plyrReady.subscribe(() => {
        if (!!this.plyr) {
          if (!!this.timestamp) {
            this.plyr.player.embed.on('loaded', () => {
              // have to do this check again
              if (!!this.plyr && !!this.timestamp) {
                // if video is viewed with a timestamp in the query, jump to it
                this.plyr.player.currentTime = this.timestamp;
              }
            });
          }
          if (this.autoplay && this.isDesktop()) {
            // try to autoplay this video if wanted
            // also, only autoplay in case this is a desktop, as mobile browsers mute videos on autoplay
            this.handlePlay(this.plyr.player).then();
          }
        }
      });
    }
  }

  /**
   * Tries to play the video in the given Plyr-instance.
   * Used to handle the autoplay-functionality without throwing common errors.
   * play()'s promise returns an error if:
   * - Autoplay is disabled (iOS, manually set by user, ...)
   * - User didn't interact with the app first (e.g. directly access view via link without clicking during load)
   * - Some weird combination of switching tabs while video loads ('call to play() was interrupted ...')
   * We definitely don't want those to land in reporting, as they have no further impact on the page and should just be ignored.
   * @param player Bound instance of a Plyr
   */
  private async handlePlay(player: Plyr) {
    try {
      await player.play();
    } catch (e) {
      console.error(e);
    }
  }

  isDesktop() {
    return this._deviceDetector.isDesktop();
  }
}
